export const ASSET_INVENTORY_URL = '/asset-manager'

export const ASSET_CATEGORIES = {
  BUSINESS_PROCESS: 'business_process',
  INFORMATION: 'information',
  HARDWARE: 'hardware',
  SOFTWARE: 'software',
  NETWORK_COMMUNICATIONS: 'network_communications',
  PERSONNEL: 'personnel',
  PHYSICAL_FACILITIES: 'physical_facilities',
  SUPPLIERS: 'suppliers',
} as const

export type IAssetCategory = (typeof ASSET_CATEGORIES)[keyof typeof ASSET_CATEGORIES]

// Ordered values backing the `asset_category` Postgres enum (see src/db/schema/tables/assetManager.ts)
export const ASSET_CATEGORY_VALUES = [
  ASSET_CATEGORIES.BUSINESS_PROCESS,
  ASSET_CATEGORIES.INFORMATION,
  ASSET_CATEGORIES.HARDWARE,
  ASSET_CATEGORIES.SOFTWARE,
  ASSET_CATEGORIES.NETWORK_COMMUNICATIONS,
  ASSET_CATEGORIES.PERSONNEL,
  ASSET_CATEGORIES.PHYSICAL_FACILITIES,
  ASSET_CATEGORIES.SUPPLIERS,
] as const

// User-facing asset code prefix per category (e.g. HW01, SW12). Sequential per organization.
// English abbreviations of the category names (BP = business process, NET = network, SP = suppliers, ...).
export const ASSET_CATEGORY_CODE_PREFIXES: Record<IAssetCategory, string> = {
  [ASSET_CATEGORIES.BUSINESS_PROCESS]: 'BP',
  [ASSET_CATEGORIES.INFORMATION]: 'IN',
  [ASSET_CATEGORIES.HARDWARE]: 'HW',
  [ASSET_CATEGORIES.SOFTWARE]: 'SW',
  [ASSET_CATEGORIES.NETWORK_COMMUNICATIONS]: 'NET',
  [ASSET_CATEGORIES.PERSONNEL]: 'P',
  [ASSET_CATEGORIES.PHYSICAL_FACILITIES]: 'PF',
  [ASSET_CATEGORIES.SUPPLIERS]: 'SP',
}

export const ASSET_CLASSIFICATIONS = {
  INTERNAL: 'internal',
  PUBLIC: 'public',
  CONFIDENTIAL: 'confidential',
} as const

export type IAssetClassification = (typeof ASSET_CLASSIFICATIONS)[keyof typeof ASSET_CLASSIFICATIONS]

// Ordered values backing the `asset_classification` Postgres enum (see src/db/schema/tables/assetManager.ts)
export const ASSET_CLASSIFICATION_VALUES = [
  ASSET_CLASSIFICATIONS.INTERNAL,
  ASSET_CLASSIFICATIONS.PUBLIC,
  ASSET_CLASSIFICATIONS.CONFIDENTIAL,
] as const

// Sequence number is zero-padded to this width in the asset code (e.g. HW01, SW12, HW100).
export const ASSET_CODE_NUMBER_PADDING = 2

export const ASSET_SCORE_MIN = 1
export const ASSET_SCORE_MAX = 5

// Rating dimensions in display order. `letter` is the ISO shorthand (D = disponibilidad).
export const ASSET_SCORE_DIMENSIONS = [
  { field: 'confidentialityScore', letter: 'C', isOptional: false },
  { field: 'integrityScore', letter: 'I', isOptional: false },
  { field: 'availabilityScore', letter: 'D', isOptional: false },
  { field: 'authenticityScore', letter: 'A', isOptional: true },
  { field: 'traceabilityScore', letter: 'T', isOptional: true },
] as const

export type IAssetScoreField = (typeof ASSET_SCORE_DIMENSIONS)[number]['field']

export const ASSET_RATING_SEVERITIES = {
  CRITICAL: 'critical',
  HIGH: 'high',
  MEDIUM: 'medium',
  LOW: 'low',
  VERY_LOW: 'very_low',
} as const

export type IAssetRatingSeverity = (typeof ASSET_RATING_SEVERITIES)[keyof typeof ASSET_RATING_SEVERITIES]

// Sentinel for the "no severity filter" option in the Asset Inventory severity strip.
export const ASSET_SEVERITY_FILTER_ALL = 'all' as const

export type IAssetSeverityFilter = IAssetRatingSeverity | typeof ASSET_SEVERITY_FILTER_ALL

// Applied to the rounded (1-decimal) rating so the shown number and the severity always agree.
export const ASSET_RATING_THRESHOLDS: readonly { min: number; severity: IAssetRatingSeverity }[] = [
  { min: 4.6, severity: ASSET_RATING_SEVERITIES.CRITICAL },
  { min: 3.8, severity: ASSET_RATING_SEVERITIES.HIGH },
  { min: 2.8, severity: ASSET_RATING_SEVERITIES.MEDIUM },
  { min: 2, severity: ASSET_RATING_SEVERITIES.LOW },
  { min: 1, severity: ASSET_RATING_SEVERITIES.VERY_LOW },
]
